import { Button, ButtonProps } from "@mui/material";
import styles from "./RadioCard.module.css";
import { cloneElement, ComponentPropsWithoutRef, forwardRef, isValidElement, ReactElement } from "react";
import CheckmarkIcon from "../images/checkmark.svg?react";
import i18nPackage from "../../i18n/i18n";

export type CoreRadioCardIconProps = {
	icon: React.ReactNode;
	enabled: boolean;
	/** @default 40px */
	width?: number;
	/**@default 40px */
	height?: number;
	/**@default false */
	showCheckmark?: boolean;
	className?: string;
	style?: React.CSSProperties;
};

/**Adds enabled and not enabled styling. */
export function CoreRadioCardIcon({
	icon,
	enabled,
	height,
	width,
	showCheckmark = false,
	className,
	style,
}: CoreRadioCardIconProps) {
	const finalWidth = `${width ?? 40}px`;
	const finalHeight = `${height ?? 40}px`;

	// aria-hidden goes on the passed-in icon itself rather than the container, so anything else rendered
	// inside the container (e.g. the checkmark) keeps its own accessibility semantics.
	const hiddenIcon = isValidElement(icon)
		? cloneElement(icon as ReactElement<{ "aria-hidden"?: boolean | "true" | "false" }>, { "aria-hidden": "true" })
		: icon;

	return (
		<div
			style={{ ...style, width: finalWidth, height: finalHeight }}
			className={`${styles.radioIconContainer} ${className || ""} ${enabled ? styles.enabled : ""}`}
		>
			{hiddenIcon}
			{enabled && showCheckmark && (
				<div className={styles.greenCheck}>
					<CheckmarkIcon />
				</div>
			)}
		</div>
	);
}

type CoreRadioCardSelectIconProps = {
	selected: boolean;
	/**If true, will show empty circle when not selected. @default false */
	showUnselectedUI?: boolean;
	/**@default 24px */
	width?: string | number;
	/**@default 24px */
	height?: string | number;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

/**Purple checkmark */
export function CoreRadioCardSelectIcon({
	selected,
	width = 24,
	height = 24,
	showUnselectedUI,
	...props
}: CoreRadioCardSelectIconProps) {
	const finalWidth = typeof width === "number" ? `${width}px` : width;
	const finalHeight = typeof height === "number" ? `${height}px` : height;

	const isVisible = selected || showUnselectedUI;

	const classNames = [styles.selectIconWrapper];
	if (selected) classNames.push(styles.selected);
	if (!selected) classNames.push(styles.notSelected);
	if (!isVisible) classNames.push(styles.hidden);
	if (props.className) classNames.push(props.className);

	const finalClassName = classNames.join(" ");

	return (
		<div
			{...props}
			className={finalClassName}
			style={{ ...props.style, width: finalWidth, height: finalHeight }}
		>
			{selected &&
				<CheckmarkIcon
					role="img"
					aria-label={i18nPackage.t("coreComponents.radioCard.checkedAriaLabel")}
				/>
			}
		</div>
	);
}

type CoreRadioCardProps = {
	selected: boolean;
	onClick?: () => void;
	disabled?: boolean;
	children: React.ReactNode;
	secondaryAction?: React.ReactNode;
	slotProps?: {
		container?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		button?: Omit<ButtonProps, "onClick" | "children">;
		actionWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
	};
};

const CoreRadioCard = forwardRef<HTMLDivElement, CoreRadioCardProps>(function CoreRadioCard(
	{ children, onClick, secondaryAction, selected, disabled, slotProps },
	ref,
) {
	const containerClassNames = [styles.radioCardContainer];

	if (selected) containerClassNames.push(styles.selected);
	if (!selected) containerClassNames.push(styles.notSelected);
	if (disabled) containerClassNames.push(styles.disabled);
	if (slotProps?.container?.className) containerClassNames.push(slotProps.container.className);

	const containerClassName = containerClassNames.join(" ");

	// Action is out of button because accessibility says no clickables in clickables
	return (
		<div {...slotProps?.container} className={containerClassName} ref={ref}>
			<Button
				{...slotProps?.button}
				onClick={onClick}
				className={`${styles.radioCardButton}  ${slotProps?.button?.className || ""}`}
				aria-pressed={selected}
				disabled={disabled}
			>
				{children}
			</Button>
			{secondaryAction && (
				<div className={`${styles.secondaryActionWrapper} ${slotProps?.actionWrapper?.className || ""}`}>
					{secondaryAction}
				</div>
			)}
		</div>
	);
});

export default CoreRadioCard;
